import React from 'react';
import * as allIcons from '@material-ui/icons/';
import { toSnakeCase } from 'util/string';
import {
  faFileAlt,
  faFileExcel,
  faFilePdf,
  faFilePowerpoint,
  faFileWord,
  faImage,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function DynamicIcon({
  icon,
  iconId,
  insideColoredButton,
  title,
  style,
  className,
}) {
  const [iconComponent, setIconComponent] = React.useState();

  React.useEffect(() => {
    if (icon && icon.match(/^https?:\/\//)) {
      setIconComponent(
        <img style={style} className={className} alt={title} src={icon} />
      );
      return;
    }
    const iconName = icon || iconNames[iconId] || toSnakeCase(title);
    (insideColoredButton
      ? import(`assets/icons-white/${iconName}.svg`)
      : import(`assets/icons/${iconName}.svg`)
    )
      .then((iconFile) =>
        setIconComponent(
          <img
            style={style}
            className={className}
            alt={title || iconName}
            src={iconFile.default}
          />
        )
      )
      .catch((error) => {
        const MaterialIcon = allIcons[iconName] || allIcons.Help;
        setIconComponent(
          <MaterialIcon
            style={{ ...style, color: (style || {}).color || '#909090' }}
            className={className}
          />
        );
      });
  }, [icon, iconId, title, style, className]);

  return iconComponent || <></>;
}

const iconNames = {
  1000: 'default_icon',
  0: 'Add',
  1: 'Save',
  2: 'default_avatar',
  3: 'notification',
  4: 'edit',
  5: 'chat',
  6: 'delete',
  7: 'attach',
  8: 'logout',
  9: 'dashboard',
  10: 'account',
  11: 'Home',
  12: 'Work',
  13: 'PinDrop',
  100: 'location',
  101: 'user',
  102: 'team',
  103: 'asset',
  104: 'inventory',
  105: 'meter',
  106: 'vendor',
  107: 'customer',
  108: 'workorder',
  109: 'attendance',
  110: 'selectedDashboard',
  111: 'ppm',
  112: 'breakdown',
  113: 'location',
  114: 'add_to_aashboard',
  115: 'add_workorder',
  116: 'verify',
  117: 'closed',
  118: 'timer',
  119: 'checklist',
  120: 'asset',
  145: 'right-arrow',
  146: 'content',
  147: 'selectedContent',
  148: 'Add',
  149: 'edit-pencil',
  150: 'Delete',
  151: 'client-comments',
  152: 'grey-right-arrow',
  153: 'document',
  154: 'instructions',
  155: 'rightArr-blue',
  156: 'shrink',
  157: 'expand',
  158: 'cancel',
  159: 'remove',
  160: 'filter',
  161: 'Autorenew',
  162: 'selectedFilter',
  163: 'HelpOutline',
  164: 'taxes',
  165: 'taxesSelected',
  166: 'prescription',
  180: 'EditOutlined',
  167: 'ShoppingCart',
  168: 'RemoveRedEye',
  169: 'LocalShipping',
  170: 'CheckCircle',
  171: 'qrCode',
  172: 'GroupAdd',
  173: 'settings',
  174: 'settingsSelected',
  175: 'duePayments',
  900: 'leftArr-blue',
  901: 'leftArr-grey',
  902: 'rightArr-grey',
  905: 'assistant-blue',
  906: 'assistant-grey',
  907: 'reminder-bell',
  908: 'send_message',
  909: 'gallery',
  910: 'new-message',
  911: 'message',
  912: 'refresh',
  915: 'addWhite',
  917: 'import',
  918: 'GetApp',
  919: 'Chat',
  920: 'mappingPeople',
  921: 'mappingPeopleSelected',
  922: 'rules',
  923: 'rulesSelected',
  924: 'graphs',
  925: 'graphsSelected',
  926: 'hierarchy',
  927: 'hierarchySelected',
  928: 'CheckCircleOutlineOutlined',
  929: 'SentimentSatisfiedOutlined',
  930: 'Refresh',
  931: 'Error',
  932: 'GetApp',
  933: 'ExpandMore',
  934: 'MoreVert',
  935: 'FileCopy',
  936: 'AddShoppingCart',
  937: 'createOrder',
  938: 'createOrderSelected',
  939: 'FormatListBulleted',
  940: 'pharmacyCatalog',
  941: 'pharmacyCatalogSelected',
  942: 'CreditCard',
  943: 'generateInvoice',
  944: 'invoice',
  945: 'bill',
  946: 'billSelected',
  947: 'delivered',
  948: 'Timeline',
  949: 'bouncedOrders',
  950: 'bouncedOrdersSelected',
  951: 'confirmOrder',
  952: 'cancelOrder',
  953: 'orderTracking',
  954: 'orderTrackingSelected',
  955: 'duePaymentsSelected',
  956: 'pendingOrdersSelected',
  957: 'pendingOrders',
  958: 'noResultsSelected',
  959: 'billHistory',
  960: 'billHistorySelected',
  961: 'Print',
  962: 'inventoryCheck',
  963: 'inventoryCheckSelected',
  964: 'billing',
  965: 'billingSelected',
  966: 'returns',
  967: 'returnsSelected',
  968: 'purchase',
  969: 'purchaseSelected',
  970: 'expired',
  971: 'expiredSelected',
  972: 'return',
  973: 'returnSelected',
};

export const FileTypeIcons = ({ type, style }) => {
  const iconsCollection = {
    'application/pdf': { icon: faFilePdf, color: '#b70000' },
    'image/png': { icon: faImage, color: '#9999ff' },
    'image/jpeg': { icon: faImage, color: '#9999ff' },
    'image/bmp': { icon: faImage, color: '#9999ff' },
    'application/msword': { icon: faFileWord, color: '#6060ff' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
      icon: faFileWord,
      color: '#6060ff',
    },
    'application/vnd.ms-excel': { icon: faFileExcel, color: '#1d6f42' },
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
      icon: faFileExcel,
      color: '#1d6f42',
    },
    'application/vnd.ms-powerpoint': {
      icon: faFilePowerpoint,
      color: '#d04423',
    },
    'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      {
        icon: faFilePowerpoint,
        color: '#d04423',
      },
  };

  const renderIcon = iconsCollection[type] || {
    icon: faFileAlt,
    color: '#e8e8e8',
  };

  return (
    <FontAwesomeIcon
      icon={renderIcon.icon}
      style={{ color: renderIcon.color, ...style }}
    />
  );
};
